// src/components/blog/features/NewsletterSection.tsx

'use client'

import { motion, AnimatePresence } from 'motion/react'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import { cn } from '~/lib/utils'

interface NewsletterSectionProps {
  compact?: boolean
  className?: string
}

export function NewsletterSection({ compact, className }: NewsletterSectionProps): JSX.Element {
  if (compact) {
    return (
      <section className={cn(
        "overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 p-6 dark:from-blue-900 dark:to-indigo-900",
        className
      )}>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white">
            Stay Updated
          </h3>
          <p className="mt-2 text-sm text-blue-100">
            Get the latest insights delivered to your inbox.
          </p>
          <BaseEmailCaptureForm
            id="newsletter-compact"
            name="newsletter-compact"
            template={{
              templateAlias: 'newsletter-minimal',
              from: 'info@adsights.ai', // TODO: Change to newsletter@adsights.ai once Postmark is set up for this
              to: '', // Will be filled by form
              templateModel: {
                base: {
                  name: '', // Will be filled by form
                  product_name: 'AdSights',
                  product_url: 'https://adsights.ai',
                  company_name: 'AdSights',
                  company_address: 'AdSights Inc., 254 Chapman Rd, Ste 208 #28056, Newark, Delaware, 19702',
                  support_email: 'support@adsights.ai',
                  sender_name: 'AdSights Team',
                  logo_url: '/logo.png'
                }
              }
            }}
            theme={{
              variant: 'embedded',
              layout: 'stacked',
              size: 'md',
              theme: 'light',
              className: {
                form: "mt-4",
                input: "w-full rounded-lg border-0 bg-white/10 px-4 py-2 text-white shadow-sm ring-1 ring-inset ring-white/20 placeholder:text-blue-200 focus:ring-2 focus:ring-inset focus:ring-white sm:text-sm",
                button: "mt-3 w-full rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50 disabled:cursor-not-allowed",
                success: "mt-3 text-xs text-blue-200",
                error: "mt-3 text-xs text-red-200"
              }
            }}
            content={{
              button: {
                text: "Subscribe",
                loadingText: "Subscribing..."
              },
              messages: {
                success: "Subscribed!",
                error: "Try again"
              }
            }}
            features={{
              animation: {
                enabled: true,
                type: 'fade',
                duration: 300
              }
            }}
          >
            {({ state, handlers, config }) => (
              <div>
                <input
                  type="email"
                  value={state.email}
                  onChange={handlers.handleChange}
                  onBlur={handlers.handleBlur}
                  placeholder="Enter your email"
                  className={config.theme?.className?.input}
                />
                <button
                  type="submit"
                  disabled={state.isSubmitting}
                  className={config.theme?.className?.button}
                >
                  {state.isSubmitting ? config.content?.button.loadingText : config.content?.button.text}
                </button>
                <AnimatePresence>
                  {state.isSuccess && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={config.theme?.className?.success}
                    >
                      {config.content?.messages.success}
                    </motion.p>
                  )}
                  {state.error && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={config.theme?.className?.error}
                    >
                      {config.content?.messages.error}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            )}
          </BaseEmailCaptureForm>
        </div>
      </section>
    )
  }

  return (
    <section className={cn(
      "relative isolate overflow-hidden rounded-2xl bg-gradient-to-b from-blue-600 to-blue-800 px-6 py-12 dark:from-blue-900 dark:to-blue-950",
      className
    )}>
      <div className="absolute inset-0 -z-10 opacity-20">
        <svg
          className="h-full w-full"
          viewBox="0 0 1097 845"
          aria-hidden="true"
          fill="none"
        >
          <path
            d="M301.147 442.302c-110.842-37.885-190.147-146.302-190.147-273.302h580l-389.853 273.302z"
            fill="url(#newsletter-gradient-1)"
          />
          <defs>
            <linearGradient
              id="newsletter-gradient-1"
              x1="1097"
              y1="0"
              x2="0"
              y2="845"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4F46E5" />
              <stop offset="1" stopColor="#80CAFF" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Get the Latest Insights
        </h2>
        <p className="mt-4 text-lg text-blue-100">
          Subscribe to our newsletter for expert analysis, industry trends, and actionable strategies to improve your ad creative performance.
        </p>
        <BaseEmailCaptureForm
          id="newsletter-full"
          name="newsletter-full"
          template={{
            templateAlias: 'newsletter-standard',
            from: 'info@adsights.ai', // TODO: Change to newsletter@adsights.ai once Postmark is set up for this
            to: '', // Will be filled by form
            templateModel: {
              base: {
                product_name: 'AdSights',
                product_url: 'https://www.adsights.ai',
                company_name: 'AdSights',
                company_address: 'AdSights Inc., 254 Chapman Rd, Ste 208 #28056, Newark, Delaware, 19702',
                support_email: 'support@adsights.ai',
                sender_name: 'AdSights Team',
                logo_url: '/logo.png'
              }
            }
          }}
          theme={{
            variant: 'embedded',
            layout: 'inline',
            size: 'lg',
            theme: 'light',
            className: {
              form: "mt-8 flex max-w-md mx-auto items-end gap-x-4",
              input: "block w-full rounded-lg border-0 bg-white/10 px-4 py-3 text-white shadow-sm ring-1 ring-inset ring-white/20 placeholder:text-blue-200 focus:ring-2 focus:ring-inset focus:ring-white sm:text-sm",
              button: "rounded-lg bg-white px-4 py-3 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50 disabled:cursor-not-allowed",
              success: "mt-4 text-sm text-blue-200",
              error: "mt-4 text-sm text-red-200"
            }
          }}
          content={{
            button: {
              text: "Subscribe",
              loadingText: "Subscribing..."
            },
            messages: {
              success: "Thanks for subscribing! Please check your email to confirm your subscription.",
              error: "Something went wrong. Please try again later."
            }
          }}
          features={{
            animation: {
              enabled: true,
              type: 'fade',
              duration: 300
            }
          }}
        >
          {({ state, handlers, config }) => (
            <div className="flex w-full items-end gap-x-4">
              <div className="flex-1">
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={state.email}
                  onChange={handlers.handleChange}
                  onBlur={handlers.handleBlur}
                  className={config.theme?.className?.input}
                  placeholder="Enter your email"
                />
              </div>
              <button
                type="submit"
                disabled={state.isSubmitting}
                className={config.theme?.className?.button}
              >
                {state.isSubmitting ? config.content?.button.loadingText : config.content?.button.text}
              </button>
              <AnimatePresence>
                {state.isSuccess && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={config.theme?.className?.success}
                  >
                    {config.content?.messages.success}
                  </motion.p>
                )}
                {state.error && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={config.theme?.className?.error}
                  >
                    {config.content?.messages.error}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          )}
        </BaseEmailCaptureForm>
      </div>
    </section>
  )
} 